import React, { useRef, useState } from "react";

/**
 * Upload or paste a URL for a Discord embed large image (setImage).
 */
export default function EmbedImageField({
  label = "EMBED IMAGE",
  helpText,
  imageUrl,
  onUrlChange,
  onUploaded,
  onCleared,
  uploading,
  setUploading,
  guildId,
  kind,
  apiBase,
}) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");

  const uploadFile = async (file) => {
    if (!file || !guildId) return;
    setError("");
    setUploading?.(true);
    try {
      const form = new FormData();
      form.append("image", file);
      form.append("kind", kind);
      const res = await fetch(`${apiBase}/settings/${guildId}/embed-image`, {
        method: "POST",
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Upload failed");
      }
      onUploaded?.(data.url);
      onUrlChange?.(data.url);
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading?.(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clearImage = async () => {
    setError("");
    setUploading?.(true);
    try {
      if (guildId) {
        await fetch(`${apiBase}/settings/${guildId}/embed-image/${kind}`, {
          method: "DELETE",
        });
      }
      onCleared?.();
      onUrlChange?.("");
    } catch (err) {
      setError(err.message || "Could not clear image");
    } finally {
      setUploading?.(false);
    }
  };

  return (
    <div style={{ marginBottom: "12px" }}>
      <label className="input-label">{label}</label>
      {helpText && (
        <p
          className="help-text"
          style={{ color: "#7b6f62", fontSize: "0.75rem", marginTop: "-4px" }}
        >
          {helpText}
        </p>
      )}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <button
          type="button"
          className="settings-input"
          style={{ width: "auto", cursor: "pointer", padding: "8px 14px" }}
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? "Uploading…" : "Upload image"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) uploadFile(file);
          }}
        />
        {imageUrl ? (
          <button
            type="button"
            className="settings-input"
            style={{ width: "auto", cursor: "pointer", padding: "8px 14px" }}
            disabled={uploading}
            onClick={clearImage}
          >
            Remove image
          </button>
        ) : null}
      </div>

      <input
        className="settings-input"
        value={imageUrl || ""}
        onChange={(e) => onUrlChange?.(e.target.value)}
        placeholder="Or paste an image URL (https://…)"
      />

      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          style={{
            marginTop: "10px",
            maxWidth: "100%",
            maxHeight: "140px",
            borderRadius: "8px",
            objectFit: "cover",
            border: "1px solid rgba(123,111,98,0.35)",
          }}
        />
      ) : null}

      {error ? (
        <p style={{ color: "#c45c5c", fontSize: "0.8rem", marginTop: "6px" }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
